import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { StatCard } from '../../components/ui/stat-card';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  MerchantSummaryTable,
  type MerchantSummaryRow,
} from '../../components/MerchantSummaryTable';

interface StatsResponse {
  items: Array<{
    deposit: number;
    topup: number;
    withdraw: number;
    settle: number;
    feeSeller: number;
  }>;
  total: number;
  payinFeePercent: number;
  payoutFeePercent: number;
  lastStatSyncAt: string | null;
}

function getMonthOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    opts.push({ value, label });
  }
  return opts;
}

function pad(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRangeForMonth(yearMonth: string): {
  dateFrom: string;
  dateTo: string;
  isCurrent: boolean;
} {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0);
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrent ? now : lastDay;
  return { dateFrom: `${yearMonth}-01`, dateTo: pad(endDate), isCurrent };
}

// Returns the freshness cutoff: a sync at-or-after this Date covers the period fully.
// - Past month: start of next month in GMT+7
// - Current month: start of today in GMT+7
function syncCutoff(yearMonth: string): Date {
  const [year, month] = yearMonth.split('-').map(Number);
  const now = new Date();
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
  if (isCurrent) {
    // Start of today in GMT+7 → as UTC
    const offset = 7 * 60 * 60 * 1000;
    const shifted = new Date(now.getTime() + offset);
    const startOfDayUtc = Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate(),
    );
    return new Date(startOfDayUtc - offset);
  }
  // Start of (month+1)-01 in GMT+7
  return new Date(`${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00.000+07:00`);
}

export function SellerDashboard() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(selectedMonth);

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['seller', 'stats', dateFrom, dateTo],
    queryFn: () => apiGet<StatsResponse>('/seller/stats', { dateFrom, dateTo, pageSize: '100' }),
  });

  const { data: subSellerData } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () => apiGet<{ items: unknown[]; total: number }>('/seller/sub-sellers'),
  });

  const { data: merchantSummaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['seller', 'merchant-summary', dateFrom, dateTo],
    queryFn: () =>
      apiGet<{
        items: MerchantSummaryRow[];
        payinFeePercent: number;
        payoutFeePercent: number;
      }>('/seller/merchants/summary', { dateFrom, dateTo }),
  });

  const queryClient = useQueryClient();
  const syncMutation = useMutation({
    mutationFn: () =>
      apiPost<{ synced: number; errors: string[] }>('/seller/sync/stats', {
        queryParams: { date: new Date(`${selectedMonth}-01T00:00:00.000+07:00`).toISOString() },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'stats'] });
      }
    },
  });

  const syncMerchantStatsMutation = useMutation({
    mutationFn: () =>
      apiPost<{ synced: number; merchantsProcessed: number; errors: string[] }>(
        '/seller/sync/merchant-stats',
        {
          queryParams: { date: new Date(`${selectedMonth}-01T00:00:00.000+07:00`).toISOString() },
        },
      ),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'merchant-summary'] });
      }
    },
  });

  const stats = statsData?.ok ? statsData.data.items : [];
  const payinPct = statsData?.ok ? statsData.data.payinFeePercent : 0;
  const payoutPct = statsData?.ok ? statsData.data.payoutFeePercent : 0;
  const lastSyncStr = statsData?.ok ? statsData.data.lastStatSyncAt : null;
  const lastSync = lastSyncStr ? new Date(lastSyncStr) : null;
  const subSellerCount = subSellerData?.ok ? subSellerData.data.total : 0;

  const totalDeposit = stats.reduce((sum, s) => sum + s.deposit, 0);
  const totalTopup = stats.reduce((sum, s) => sum + s.topup, 0);
  const totalWithdraw = stats.reduce((sum, s) => sum + s.withdraw, 0);
  const totalSettle = stats.reduce((sum, s) => sum + s.settle, 0);
  const totalFee = stats.reduce((sum, s) => sum + s.feeSeller, 0);
  const payinFeeAmount = ((totalDeposit + totalTopup) * payinPct) / 100;
  const payoutFeeAmount = ((totalWithdraw + totalSettle) * payoutPct) / 100;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Sync status
  let syncStatus: 'never' | 'partial' | 'fresh' = 'fresh';
  if (!lastSync) {
    syncStatus = 'never';
  } else if (lastSync < syncCutoff(selectedMonth)) {
    syncStatus = 'partial';
  }
  const lastSyncLabel = lastSync
    ? lastSync.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} size="sm">
            {syncMutation.isPending ? t('common.loading') : t('admin.syncStats')}
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400">
        {dateFrom} ~ {dateTo}
      </div>

      {/* Sync status banner */}
      {!isLoading && syncStatus === 'never' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <div className="font-medium">{t('dashboard.syncNever')}</div>
          <div className="mt-1 text-xs">{t('dashboard.syncNeverHint')}</div>
        </div>
      )}
      {!isLoading && syncStatus === 'partial' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <div className="font-medium">{t('dashboard.syncPartial')}</div>
          <div className="mt-1 text-xs">
            {t('dashboard.lastSyncAt', { time: lastSyncLabel })} — {t('dashboard.syncPartialHint')}
          </div>
        </div>
      )}
      {!isLoading && syncStatus === 'fresh' && lastSyncLabel && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-xs text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('dashboard.syncFresh', { time: lastSyncLabel })}
        </div>
      )}

      {syncMutation.isSuccess && syncMutation.data?.ok && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('admin.syncStats')}: {syncMutation.data.data.synced} records
          {syncMutation.data.data.errors.length > 0 &&
            ` (${syncMutation.data.data.errors.length} errors)`}
        </div>
      )}
      {syncMutation.isSuccess && !syncMutation.data?.ok && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {syncMutation.data?.error?.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('dashboard.totalDeposits')}
          value={formatNum(totalDeposit)}
          icon="D"
          accentClass="bg-gradient-to-tr from-emerald-600 to-emerald-400"
        />
        <StatCard
          label={t('dashboard.totalTopups')}
          value={formatNum(totalTopup)}
          icon="T"
          accentClass="bg-gradient-primary"
        />
        <StatCard
          label={t('dashboard.totalWithdraws')}
          value={formatNum(totalWithdraw)}
          icon="W"
          accentClass="bg-gradient-to-tr from-orange-600 to-amber-400"
        />
        <StatCard
          label={t('dashboard.totalSettles')}
          value={formatNum(totalSettle)}
          icon="S"
          accentClass="bg-gradient-to-tr from-violet-600 to-fuchsia-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={`${t('transactions.calcPayinFee')} (${payinPct}%)`}
          value={formatNum(payinFeeAmount)}
          hint={`(${t('transactions.deposit')} + ${t('transactions.topup')}) × ${payinPct}%`}
          icon="↘"
          accentClass="bg-gradient-primary"
        />
        <StatCard
          label={`${t('transactions.calcPayoutFee')} (${payoutPct}%)`}
          value={formatNum(payoutFeeAmount)}
          hint={`(${t('transactions.withdraw')} + ${t('transactions.settle')}) × ${payoutPct}%`}
          icon="↗"
          accentClass="bg-gradient-to-tr from-emerald-600 to-emerald-400"
        />
        <StatCard
          label={t('dashboard.totalFees')}
          value={formatNum(totalFee)}
          hint={`${t('seller.subSellerCount')}: ${subSellerCount}`}
          icon="∑"
          accentClass="bg-gradient-to-tr from-violet-600 to-fuchsia-500"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>{t('merchants.summaryTitle')}</CardTitle>
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                syncMerchantStatsMutation.mutate();
              }}
              disabled={syncMerchantStatsMutation.isPending}
            >
              {syncMerchantStatsMutation.isPending
                ? t('common.loading')
                : t('merchants.syncMerchantStats')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <MerchantSummaryTable
            rows={merchantSummaryData?.ok ? merchantSummaryData.data.items : []}
            payinFeePercent={merchantSummaryData?.ok ? merchantSummaryData.data.payinFeePercent : payinPct}
            payoutFeePercent={
              merchantSummaryData?.ok ? merchantSummaryData.data.payoutFeePercent : payoutPct
            }
            isLoading={summaryLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}
